import { Docx } from '@tiptap-pro/extension-export-docx';
import type { CustomNode } from '@tiptap-pro/extension-export-docx';
import type { JSONContent } from '@tiptap/core';

import { SLASH_FULL_DATE_FORMAT } from 'core/constants';
import { formatFloating } from 'utils/formatters/timezone';

import { getColor, getLabel } from 'containers/RiskAssessmentsDetails/RiskMatrix/styles';
import type { CellValue, Column, Row } from 'containers/Tiptap/components/tiptap-node/risk-assessment-node/types';
import { createTableSpacer } from 'containers/Tiptap/lib/export-styles';
import { isRiskRatingValue } from 'containers/Tiptap/components/tiptap-node/risk-assessment-node/utils';

// `Docx` is the exporter's own namespace — see the comment in export-utils.ts
// for why a top-level `docx` instance still fails here. Re-verified 2026-10-02; see RAF-22.
const {
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  ShadingType,
  BorderStyle,
  VerticalAlign,
} = Docx;

type Paragraph = Docx.Paragraph;
type Table = Docx.Table;
type TableRow = Docx.TableRow;
type TableCell = Docx.TableCell;

function formatFileSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return ` (${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]})`;
}

function extractTextFromContent(content?: JSONContent[]): string {
  if (!content) return '';
  return content
    .map((child) => {
      if (child.type === 'text') return child.text || '';
      if (child.content) return extractTextFromContent(child.content);
      return '';
    })
    .join('');
}

function cssColorToHex(cssColor: string): string {
  if (cssColor === 'white') return 'FFFFFF';
  if (cssColor === 'black') return '000000';
  return cssColor.replace('#', '');
}

function getRiskColorForDocx(
  impact: number,
  likelihood: number,
  matrixRows: number,
  matrixColumns: number
): { bg: string; color: string; label: string } {
  const { bg, color } = getColor(impact, likelihood, matrixRows, matrixColumns);
  const label = getLabel(impact, likelihood, matrixRows, matrixColumns);
  return { bg: cssColorToHex(bg), color: cssColorToHex(color), label };
}

const THIN_BORDER = {
  style: BorderStyle.SINGLE,
  size: 1,
  color: 'CCCCCC',
};

const CELL_BORDERS = {
  top: THIN_BORDER,
  bottom: THIN_BORDER,
  left: THIN_BORDER,
  right: THIN_BORDER,
};

const NO_BORDERS = {
  top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
};

// A4 usable width ≈ 7920 DXA (with 1-inch margins on each side)
const PAGE_WIDTH_DXA = 7920;

function createCell(
  text: string,
  widthDxa: number,
  options?: {
    bold?: boolean;
    fontSize?: number;
    bgColor?: string;
    textColor?: string;
    noBorders?: boolean;
    isLastRow?: boolean;
  }
): TableCell {
  const { bold, fontSize = 18, bgColor, textColor, noBorders, isLastRow } = options ?? {};

  return new TableCell({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text,
            size: fontSize,
            bold,
            color: textColor,
            font: { name: 'Arial' },
          }),
        ],
        alignment: AlignmentType.CENTER,
        keepNext: !isLastRow,
        keepLines: true,
      }),
    ],
    width: { size: widthDxa, type: WidthType.DXA },
    verticalAlign: VerticalAlign.CENTER,
    shading: bgColor ? { type: ShadingType.SOLID, color: bgColor, fill: bgColor } : undefined,
    borders: noBorders ? NO_BORDERS : CELL_BORDERS,
  });
}

function createRiskMatrixTable(node: JSONContent): (Paragraph | Table)[] {
  const rows: number = node.attrs?.rows ?? 5;
  const columns: number = node.attrs?.columns ?? 5;
  const rawImpactLabels = node.attrs?.impactLabels;
  const impactLabels: string[] = Array.isArray(rawImpactLabels)
    ? rawImpactLabels
    : ['Insignificant', 'Minor', 'Significant', 'Major', 'Severe'];
  const rawLikelihoodLabels = node.attrs?.likelihoodLabels;
  const likelihoodLabels: string[] = Array.isArray(rawLikelihoodLabels)
    ? rawLikelihoodLabels
    : ['Almost certain', 'Likely', 'Moderate', 'Unlikely', 'Rare'];
  const selectedImpact: number = node.attrs?.selectedImpact ?? 0;
  const selectedLikelihood: number = node.attrs?.selectedLikelihood ?? 0;
  const selectedRisk: number = node.attrs?.selectedRisk ?? 0;

  // Fixed DXA widths based on A4 usable width
  const labelWidth = 1600;
  const numWidth = 500;
  const scoreWidth = Math.floor((PAGE_WIDTH_DXA - labelWidth - numWidth) / columns);

  const tableRows: TableRow[] = [];

  for (let rowIdx = 0; rowIdx < rows; rowIdx++) {
    const likelihoodValue = rows - rowIdx;
    const likelihoodLabel = likelihoodLabels[rowIdx] || `Level ${likelihoodValue}`;

    const cells: TableCell[] = [
      createCell(likelihoodLabel, labelWidth, { bold: true, fontSize: 16 }),
      createCell(String(likelihoodValue), numWidth, { fontSize: 16 }),
    ];

    for (let colIdx = 0; colIdx < columns; colIdx++) {
      const likelihood = colIdx + 1;
      const score = likelihoodValue * likelihood;
      const { bg, color } = getRiskColorForDocx(likelihoodValue, likelihood, rows, columns);
      const isSelected = likelihoodValue === selectedImpact && likelihood === selectedLikelihood;

      cells.push(
        createCell(isSelected ? `● ${score}` : String(score), scoreWidth, {
          bold: isSelected,
          bgColor: bg,
          textColor: color,
          fontSize: 18,
        })
      );
    }

    tableRows.push(new TableRow({ children: cells, cantSplit: true }));
  }

  // Impact labels row
  tableRows.push(
    new TableRow({
      cantSplit: true,
      children: [
        createCell('', labelWidth, { noBorders: true }),
        createCell('', numWidth, { noBorders: true }),
        ...impactLabels
          .slice(0, columns)
          .map((label, i) => createCell(label || `Level ${i + 1}`, scoreWidth, { bold: true, fontSize: 16 })),
      ],
    })
  );

  // Impact numbers row (last row — no keepNext)
  tableRows.push(
    new TableRow({
      cantSplit: true,
      children: [
        createCell('', labelWidth, { noBorders: true, isLastRow: true }),
        createCell('', numWidth, { noBorders: true, isLastRow: true }),
        ...Array.from({ length: columns }, (_, i) =>
          createCell(String(i + 1), scoreWidth, { fontSize: 16, isLastRow: true })
        ),
      ],
    })
  );

  const columnWidths = [labelWidth, numWidth, ...Array.from({ length: columns }, () => scoreWidth)];

  const table = new Table({
    rows: tableRows,
    columnWidths,
    layout: 'fixed' as const,
    width: { size: PAGE_WIDTH_DXA, type: WidthType.DXA },
    borders: {
      top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
      bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
      left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
      right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
      insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
      insideVertical: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    },
  });

  const result: (Paragraph | Table)[] = [
    new Paragraph({
      children: [new TextRun({ text: 'Likelihood ↑', size: 16, italics: true, color: '888888' })],
      spacing: { before: 120, after: 40 },
      keepNext: true,
    }),
    table,
    new Paragraph({
      children: [new TextRun({ text: 'Impact →', size: 16, italics: true, color: '888888' })],
      alignment: AlignmentType.RIGHT,
      spacing: { before: 40, after: 80 },
      keepLines: true,
    }),
  ];

  if (selectedImpact && selectedLikelihood) {
    const { bg, color, label } = getRiskColorForDocx(selectedImpact, selectedLikelihood, rows, columns);

    result.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Impact: ', size: 20, color: '666666' }),
          new TextRun({ text: String(selectedImpact), size: 20, bold: true }),
          new TextRun({ text: '    Likelihood: ', size: 20, color: '666666' }),
          new TextRun({ text: String(selectedLikelihood), size: 20, bold: true }),
          new TextRun({ text: '    Risk Score: ', size: 20, color: '666666' }),
          new TextRun({
            text: ` ${selectedRisk} - ${label} `,
            size: 20,
            bold: true,
            color,
            shading: { type: ShadingType.SOLID, color: bg, fill: bg },
          }),
        ],
        spacing: { before: 120, after: 200 },
        keepLines: true,
      })
    );
  }

  return result;
}

export function formatAssessmentCell(column: Column, value: CellValue | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (column.type) {
    case 'date': {
      if (typeof value !== 'string') return String(value);
      return formatFloating(value, 'date', SLASH_FULL_DATE_FORMAT) || value;
    }
    default:
      return String(value);
  }
}

function createRiskAssessmentTable(node: JSONContent): (Paragraph | Table)[] {
  const columns: Column[] = Array.isArray(node.attrs?.columns) ? node.attrs.columns : [];
  const rows: Row[] = Array.isArray(node.attrs?.rows) ? node.attrs.rows : [];
  const likelihoodLabels: string[] = Array.isArray(node.attrs?.likelihoodLabels) ? node.attrs.likelihoodLabels : [];
  const severityLabels: string[] = Array.isArray(node.attrs?.severityLabels) ? node.attrs.severityLabels : [];

  if (columns.length === 0) {
    return [
      new Paragraph({
        children: [new TextRun({ text: 'Risk assessment (no columns configured)', italics: true, size: 18 })],
      }),
    ];
  }

  const colWidth = Math.floor(PAGE_WIDTH_DXA / columns.length);

  const headerRow = new TableRow({
    cantSplit: true,
    tableHeader: true,
    children: columns.map((col) => createCell(col.label, colWidth, { bold: true, fontSize: 16, bgColor: 'F5F5F5' })),
  });

  const bodyRows: TableRow[] =
    rows.length === 0
      ? [
          new TableRow({
            cantSplit: true,
            children: [
              createCell('No hazards recorded', PAGE_WIDTH_DXA, {
                fontSize: 16,
                textColor: '888888',
                isLastRow: true,
              }),
            ],
          }),
        ]
      : rows.map((row, rowIdx) => {
          const isLast = rowIdx === rows.length - 1;

          const cells = columns.map((col) => {
            if (col.type === 'riskRating') {
              const v = row.values[col.id];
              if (isRiskRatingValue(v) && v.likelihood !== null && v.severity !== null) {
                const score = v.likelihood * v.severity;
                const { bg, color, label } = getRiskColorForDocx(
                  v.severity,
                  v.likelihood,
                  severityLabels.length || 5,
                  likelihoodLabels.length || 5
                );
                return createCell(`${v.likelihood} × ${v.severity} = ${score} – ${label}`, colWidth, {
                  bold: true,
                  fontSize: 16,
                  bgColor: bg,
                  textColor: color,
                  isLastRow: isLast,
                });
              }
              return createCell('—', colWidth, {
                fontSize: 16,
                textColor: '888888',
                isLastRow: isLast,
              });
            }
            const text = formatAssessmentCell(col, row.values[col.id]);
            return createCell(text, colWidth, { fontSize: 16, isLastRow: isLast });
          });

          return new TableRow({ cantSplit: true, children: cells });
        });

  const table = new Table({
    rows: [headerRow, ...bodyRows],
    columnWidths: columns.map(() => colWidth),
    layout: 'fixed' as const,
    width: { size: PAGE_WIDTH_DXA, type: WidthType.DXA },
  });

  return [
    new Paragraph({
      children: [new TextRun({ text: 'Risk assessment', size: 20, bold: true })],
      spacing: { before: 120, after: 80 },
      keepNext: true,
    }),
    table,
    new Paragraph({ children: [new TextRun({ text: '' })], spacing: { after: 120 } }),
  ];
}

export const customNodes: CustomNode[] = [
  {
    type: 'codeBlock',
    render: (node) => {
      const lines = extractTextFromContent(node.content).split('\n');

      return lines.map(
        (line) =>
          new Paragraph({
            children: [
              new TextRun({
                text: line || ' ',
                font: { name: 'Courier New' },
                size: 20,
              }),
            ],
            shading: {
              type: ShadingType.SOLID,
              color: 'F5F5F5',
              fill: 'F5F5F5',
            },
            spacing: { before: 0, after: 0 },
          })
      );
    },
  },
  {
    type: 'riskMatrix',
    // CustomNode.render type is too narrow — it accepts Table elements at runtime
    render: (node) => createRiskMatrixTable(node) as Paragraph[],
  },
  {
    type: 'riskAssessment',
    // CustomNode.render type is too narrow — it accepts Table elements at runtime
    render: (node) => createRiskAssessmentTable(node) as Paragraph[],
  },
  {
    type: 'tableSpacer',
    render: () => createTableSpacer(),
  },
  {
    type: 'attachment',
    render: (node) => {
      const name: string = node.attrs?.name || node.attrs?.filename || 'attachment';
      const size: number | undefined = node.attrs?.size;

      return new Paragraph({
        children: [
          new TextRun({
            text: `📎 ${name}${formatFileSize(size)}`,
            size: 22,
          }),
        ],
        spacing: { before: 80, after: 80 },
        shading: {
          type: ShadingType.SOLID,
          color: 'F5F5F5',
          fill: 'F5F5F5',
        },
      });
    },
  },
];
